import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Separator,
} from 'app-template-lib/components/shadcn';
import parse from 'html-react-parser';
import { FETCH_T_PDCA_ID } from '../../queries/pdca_t_pdca.queries.type';
import { FETCH_M_LG_CONFIG } from '@/main/queries/setting_m_lg_config.queries.type';
import { IContentsContentElem } from 'app-template-lib/utils';
import { TContentsContentParams } from '@/shared/t-contents-params';
import { useEffect, useState } from 'react';

import { YosanChartWidget } from '../shared/organisms/chart/yosanTransitionChartWidget.presenter';
import { KPICard } from '../shared/organisms/card/kpiCard.presenter';
import { ToWarekiFormatter } from '../../utils/toWarekiFormatter';

interface IJigyoDetailPresenterProps<U> extends IContentsContentElem<U> {
    data: FETCH_T_PDCA_ID | undefined;
    jigyoYear: string;
    lgconfig: FETCH_M_LG_CONFIG | undefined;
    lgcode: string;
}

type SDGS = {
    seisaku_shisaku_name: string;
};

/**
 * 事業詳細ページ presenter
 * @param props
 * @returns
 */
export function JigyoDetail(props: IJigyoDetailPresenterProps<TContentsContentParams>) {
    const { data, jigyoYear, lgconfig, lgcode } = props;
    // const t_pdca = data?.pdca_t_pdca[0];
    const [sdgs, setSdgs] = useState<SDGS[]>([]);
    const [isKessan, setIsKessan] = useState<boolean>(false);
    const createdYear = lgconfig?.setting_m_lg_config[0].created_year || 0;

    // タブの選択状態
    const [selectedYear, setSelectedYear] = useState<string>(jigyoYear);
    // 年度と process_cd の対応を定義
    const processCdMap: Record<number, number> = {
        2024: 10, // R6年度決算
        2025: 30, // R7年度予算
        2026: 30, // R8年度予算
    };

    const t_pdca = data?.pdca_t_pdca.find(
        (item) => item.t_jigyo?.jigyo_year === Number(selectedYear) && item.process_cd === processCdMap[Number(selectedYear)]
    );

    // data に jigyo_year が入ってきたら state を更新
    // useEffect(() => {
    //     if (data?.pdca_t_pdca[0]?.t_jigyo?.jigyo_year) {
    //         setSelectedYear(String(data.pdca_t_pdca[0].t_jigyo.jigyo_year));
    //     }
    // }, [data]);

    // 事業概要をHTMLに変換
    const jigyoGaiyoToHTML: string =
        t_pdca && t_pdca.jigyo_gaiyo
            ? `${t_pdca.jigyo_gaiyo
                  .replace(new RegExp(import.meta.env.VITE_MINIO_URL, 'g'), import.meta.env.VITE_AWS_URL)
                  .replace(/table/g, "table className='border'")
                  .replace(/tr/g, "tr className='border'")
                  .replace(/td/g, "td className='border'")}`
            : '';

    const jigyohis = t_pdca?.t_jigyo.m_jisshi_keikaku.t_jigyohis.find(
        (item) => item.jigyo_year === Number(selectedYear) && item.process_cd === 30
    );

    // const kessans = t_pdca?.t_jigyo.m_jisshi_keikaku.t_kessans.find((item) => item.jigyo_year === Number(selectedYear));

    const kessans = t_pdca?.t_jigyo.m_jisshi_keikaku.t_kessans
        ?.filter((item) => item.jigyo_year === Number(selectedYear))
        ?.reduce((acc, cur) => {
            if (!acc) {
                return { ...cur };
            }
            return {
                ...acc,
                jigyohi: (acc.jigyohi ?? 0) + (cur.jigyohi ?? 0),
                zaigen_kuni: (acc.zaigen_kuni ?? 0) + (cur.zaigen_kuni ?? 0),
                zaigen_chihosai: (acc.zaigen_chihosai ?? 0) + (cur.zaigen_chihosai ?? 0),
                zaigen_ippan: (acc.zaigen_ippan ?? 0) + (cur.zaigen_ippan ?? 0),
                zaigen_ken: (acc.zaigen_ken ?? 0) + (cur.zaigen_ken ?? 0),
                zaigen_kikin: (acc.zaigen_kikin ?? 0) + (cur.zaigen_kikin ?? 0),
                zaigen_kurikoshi: (acc.zaigen_kurikoshi ?? 0) + (cur.zaigen_kurikoshi ?? 0),
                zaigen_other: (acc.zaigen_other ?? 0) + (cur.zaigen_other ?? 0),
                yosan_gengaku: (acc.yosan_gengaku ?? 0) + (cur.yosan_gengaku ?? 0),
            };
        }, null as null | (typeof t_pdca.t_jigyo.m_jisshi_keikaku.t_kessans)[number]);

    const kaikeiData = isKessan ? kessans : jigyohis;

    const sortedTSaijigyos = [...(t_pdca?.t_jigyo.t_sai_jigyos || [])]
        .filter((item) => item.process_cd === processCdMap[Number(selectedYear)])
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    const initData = (data: FETCH_T_PDCA_ID | undefined) => {
        let SDGs: {
            seisaku_shisaku_name: string;
        }[] = [];

        data?.pdca_m_keikaku.map((keikaku: { id: string; keikaku_name: string }) => {
            if (keikaku.keikaku_name === 'SDGs') {
                t_pdca?.t_jigyo.c_jimu_jigyo_seisaku_shisakus.map((item) => {
                    if (item.m_seisaku_shisaku.m_keikaku_id === keikaku.id) {
                        SDGs.push({
                            seisaku_shisaku_name: item.m_seisaku_shisaku.seisaku_shisaku_name,
                        });
                    }
                });
            }
        });

        setSdgs(SDGs);
    };

    // 初回データレンダリング時の処理
    useEffect(() => {
        if (t_pdca) {
            initData(data);
        }

        if (selectedYear <= String(createdYear - 2)) {
            setIsKessan(true);
        } else {
            setIsKessan(false);
        }
    }, [t_pdca, data]);

    return (
        <div className="pb-4">
            <section>
                <>
                    <div className="flex-col md:flex">
                        <div className="flex-1 space-y-4 pt-3">
                            <div className="flex flex-col md:flex-row md:items-left md:justify-between space-y-2 mx-2">
                                <h2 className="text-lg md:text-xl font-bold tracking-tight">{t_pdca && t_pdca.jigyo_name}</h2>
                                {/* <div className="flex items-center space-x-2">
                                    <Button>Download</Button>
                                </div> */}
                                <section id="division" className="flex">
                                    <div id="divisionName">
                                        <h3 className="md:text-md text-sm">{t_pdca?.m_soshiki_division.division_name}</h3>
                                    </div>
                                    <div id="divisionRoom" className="ml-2">
                                        <h3 className="md:text-md text-sm">{t_pdca?.tantou_kakari_main}</h3>
                                    </div>
                                </section>
                            </div>

                            <div className="grid gap-1 grid-col-1 md:grid-cols-2 lg:grid-cols-7">
                                <div className="col-span-4 mx-2">
                                    <YosanChartWidget {...props} />
                                </div>
                                <Card className="col-span-4 md:col-span-3 mx-2">
                                    <CardHeader className="flex">
                                        <CardTitle>会計</CardTitle>
                                        <CardDescription>{t_pdca?.t_jigyo.m_kaikei.kaikei_name}</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Table>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell className="font-medium" colSpan={3}>
                                                        {isKessan ? '決算額(最終予算額)' : '予算額'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.jigyohi?.toLocaleString() + '千円'}
                                                        {isKessan &&
                                                            kaikeiData &&
                                                            'yosan_gengaku' in kaikeiData &&
                                                            ' (' + kaikeiData.yosan_gengaku?.toLocaleString() + '千円)'}
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow>
                                                    <TableCell colSpan={3}>国庫支出金</TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.zaigen_kuni != null
                                                            ? kaikeiData.zaigen_kuni.toLocaleString() + '千円'
                                                            : '0千円'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={3}>県支出金</TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.zaigen_ken != null
                                                            ? kaikeiData.zaigen_ken.toLocaleString() + '千円'
                                                            : '0千円'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={3}>地方債</TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.zaigen_chihosai != null
                                                            ? kaikeiData.zaigen_chihosai.toLocaleString() + '千円'
                                                            : '0千円'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={3}>基金</TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.zaigen_kikin != null
                                                            ? kaikeiData.zaigen_kikin.toLocaleString() + '千円'
                                                            : '0千円'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={3}>その他</TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.zaigen_other != null
                                                            ? kaikeiData.zaigen_other.toLocaleString() + '千円'
                                                            : '0千円'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={3}>一般財源</TableCell>
                                                    <TableCell className="text-right">
                                                        {kaikeiData?.zaigen_ippan != null
                                                            ? kaikeiData.zaigen_ippan.toLocaleString() + '千円'
                                                            : '0千円'}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>
                </>
            </section>

            <section className="pt-4">
                <Tabs value={selectedYear} onValueChange={setSelectedYear}>
                    <TabsList className="w-full">
                        <TabsTrigger value={String(createdYear - 3)} className="w-1/4" disabled>
                            {ToWarekiFormatter(createdYear - 3)}
                        </TabsTrigger>
                        <TabsTrigger value={String(createdYear - 2)} className="w-1/4">
                            {ToWarekiFormatter(createdYear - 2)}
                        </TabsTrigger>
                        <TabsTrigger value={String(createdYear - 1)} className="w-1/4">
                            {ToWarekiFormatter(createdYear - 1)}
                        </TabsTrigger>
                        <TabsTrigger value={String(createdYear)} className="w-1/4">
                            {ToWarekiFormatter(createdYear)}
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value={selectedYear} className=" bg-secondary mt-0">
                        <Card className="shadow-none border-none mx-2 md:mx-0  bg-secondary">
                            <CardContent className="container mx-auto  grid grid-cols-1 shadow-none border-none p-3 ">
                                <Card className="shadow-none border-none rounded-md px-3 ">
                                    <h2 className="ml-2 mt-3 mb-1  font-bold text-md">対象年度</h2>
                                    <Separator />
                                    <CardContent className="p-3 text-sm mb-3 min-h-10">
                                        {selectedYear && ToWarekiFormatter(Number(selectedYear))}
                                    </CardContent>
                                </Card>
                                <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                    <h2 className="ml-2 mt-3 mb-1  font-bold text-md">まちづくりビジョン</h2>
                                    <Separator />
                                    <CardContent className="p-3 text-sm mb-3 min-h-10">
                                        {t_pdca?.t_jigyo.c_jimu_jigyo_seisaku_shisakus
                                            .filter((item) => {
                                                const keikaku = data?.pdca_m_keikaku.find(
                                                    (k) => k.id === item.m_seisaku_shisaku.m_keikaku_id
                                                );
                                                return keikaku?.keikaku_name !== 'SDGs';
                                            })
                                            .map((item, index) => (
                                                <div key={index}>{item.m_seisaku_shisaku?.seisaku_shisaku_name}</div>
                                            ))}
                                    </CardContent>
                                </Card>

                                <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                    <h2 className="ml-2 mt-3 mb-1  font-bold text-md">SDGs</h2>
                                    <Separator />
                                    {sdgs?.map((item, index) => {
                                        let number = item.seisaku_shisaku_name.match(/\d+/);
                                        return (
                                            <CardContent key={index} className="p-1  my-3 text-sm flex">
                                                <img
                                                    src={`${import.meta.env.VITE_AWS_URL}SDGs/sdg_icon_${number}.png`}
                                                    alt={`SDGs${number}`}
                                                    style={{ width: 30, height: 30, marginRight: 2, marginLeft: 2 }}
                                                />
                                                <div className="ml-3">{item.seisaku_shisaku_name}</div>
                                            </CardContent>
                                        );
                                    })}
                                </Card>

                                <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                    <h2 className="ml-2 mt-3 mb-1  font-bold text-md">事業目的</h2>
                                    <Separator />
                                    <CardContent className="p-3 text-sm mb-3 min-h-10">{t_pdca?.hitsuyosei}</CardContent>
                                </Card>

                                {isKessan === false && ['313718'].includes(lgcode) && (
                                    <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                        <h2 className="ml-2 mt-3 mb-1  font-bold text-md">前年度評価で整理した取り組みの方向</h2>
                                        <Separator />
                                        <CardContent className="text-sm p-3 mb-3 min-h-10">{t_pdca?.jigyo_memo}</CardContent>
                                    </Card>
                                )}
                                {isKessan === false && ['313718'].includes(lgcode) && (
                                    <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                        <h2 className="ml-2 mt-3 mb-1  font-bold text-md">前年度からの改善点</h2>
                                        <Separator />
                                        <CardContent className="p-3 text-sm min-h-10 mb-3">{t_pdca?.kaizen_keika}</CardContent>
                                    </Card>
                                )}

                                <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                    <h2 className="ml-2 mt-5 mb-1 font-bold text-md">細事業等</h2>
                                    <Separator className="mb-3" />
                                    {sortedTSaijigyos?.map((t_sai_jigyo) => {
                                        //予算額
                                        let yosanGokei = 0;
                                        t_sai_jigyo.c_sai_jigyo_saishutsu_yosans.forEach((yosan) => {
                                            if (!yosan._is_deleted && !yosan.t_saishutsu_yosan._is_deleted)
                                                yosanGokei += yosan.t_saishutsu_yosan.saishutsu_yosan;
                                        });

                                        return (
                                            <Card className="flex items-center shadow-none rounded-md mb-2">
                                                <CardContent className="px-0 text-sm min-h-10 min-w-full">
                                                    <CardHeader className="font-bold p-3 bg-secondary">
                                                        <CardTitle className="font-bold">
                                                            {t_sai_jigyo.saijigyo_name}{' '}
                                                            <>
                                                                {(() => {
                                                                    if (t_sai_jigyo.saijigyo_kind === 1) {
                                                                        return <text>【新規】</text>;
                                                                    } else if (t_sai_jigyo.saijigyo_kind === 2) {
                                                                        return <text>【改善】</text>;
                                                                    } else if (t_sai_jigyo.saijigyo_kind === 3) {
                                                                        return <text>【廃止】</text>;
                                                                    } else {
                                                                        return '';
                                                                    }
                                                                })()}
                                                            </>
                                                        </CardTitle>

                                                        <CardDescription className="font-medium">
                                                            {yosanGokei.toLocaleString()}千円{' '}
                                                            {t_sai_jigyo.zaigen_uchiwake && '(' + t_sai_jigyo.zaigen_uchiwake + ')'}
                                                        </CardDescription>
                                                    </CardHeader>

                                                    <section id="saijigyo_content" className="px-3">
                                                        <h3 className="font-bold pt-3">内容</h3>
                                                        <div>
                                                            {t_sai_jigyo.saijigyo_naiyo?.split('\n').map((str, index) => (
                                                                <div key={index}>
                                                                    {str}
                                                                    <br />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {/* 前年度との変更点がある場合は以下に記載 */}
                                                        {t_sai_jigyo.change_point && (
                                                            <>
                                                                <Separator className="mt-3" />
                                                                <h3 className="font-bold pt-3">前年度との変更点</h3>
                                                                <div>{t_sai_jigyo.change_point}</div>
                                                            </>
                                                        )}
                                                    </section>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}{' '}
                                </Card>

                                <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                    <h2 className="ml-2 mt-3 mb-1  font-bold text-md">
                                        {isKessan ? '事業の主な実施状況' : 'その他事業内容'}
                                    </h2>
                                    <Separator />
                                    <CardContent className="p-3 text-sm min-h-10 max-w-full mb-3">
                                        <div>{parse(jigyoGaiyoToHTML)}</div>
                                    </CardContent>
                                </Card>

                                {isKessan === true && ['313718'].includes(lgcode) && (
                                    <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                        <h2 className="ml-2 mt-3 mb-1  font-bold text-md">前年度課題の概要</h2>
                                        <Separator />
                                        <CardContent className="p-3 text-sm min-h-10 mb-3">{t_pdca?.hyoka_satei_class_b_memo}</CardContent>
                                    </Card>
                                )}
                                {isKessan === true && ['313718'].includes(lgcode) && (
                                    <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                        <h2 className="ml-2 mt-3 mb-1  font-bold text-md">前年度課題についての対応及び成果</h2>
                                        <Separator />
                                        <CardContent className="p-3 text-sm min-h-10 mb-3">{t_pdca?.hyoka_satei_class_c_memo}</CardContent>
                                    </Card>
                                )}
                                {isKessan === true && ['313718'].includes(lgcode) && (
                                    <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                        <h2 className="ml-2 mt-3 mb-1  font-bold text-md">担当課による評価の理由</h2>
                                        <Separator />
                                        <CardContent className="p-3 text-sm min-h-10 mb-3">{t_pdca?.hyoka_satei_class_d_memo}</CardContent>
                                    </Card>
                                )}

                                {isKessan === true && ['313718'].includes(lgcode) && (
                                    <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                        <h2 className="ml-2 mt-3 mb-1  font-bold text-md">今後の取り組みの方向</h2>
                                        <Separator />
                                        <CardContent className="p-3 text-sm min-h-10 mb-3">{t_pdca?.hyoka_satei_class_a_memo}</CardContent>
                                    </Card>
                                )}

                                <Card className="shadow-none border-none rounded-md px-3 mt-5">
                                    <h2 className="ml-2 mt-3 mb-1  font-bold text-md">成果指標</h2>
                                    <Card className="shadow-none border-none rounded-md mb-2">
                                        <KPICard kpis={t_pdca?.t_jigyo.m_jisshi_keikaku.t_kpi_values} />
                                    </Card>
                                </Card>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </section>
        </div>
    );
}
